/* tslint:disable */
/* eslint-disable */
/**
 * Ampli - A strong typed wrapper for your Analytics
 *
 * This file is generated by Amplitude.
 * To update run 'ampli pull kraken-web-ts'
 *
 * Required dependencies: @amplitude/analytics-browser@^1.3.0
 * Tracking Plan Version: 21
 * Build: 1.0.0
 * Runtime: browser:typescript-ampli-v2
 *
 * [View Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest)
 *
 * [Full Setup Instructions](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/implementation/kraken-web-ts)
 */

import * as amplitude from '@amplitude/analytics-browser';

export type Environment = 'development' | 'production';

export const ApiKey: Record<Environment, string> = {
  development: '46195d0a54e086957165730802316c5b',
  production: '2cf0a9667eb806e96e2ed57b99dc3fb4'
};

/**
 * Default Amplitude configuration options. Contains tracking plan information.
 */
export const DefaultConfiguration: BrowserOptions = {
  plan: {
    version: '21',
    branch: 'main',
    source: 'kraken-web-ts',
    versionId: 'cba2e503-8c10-430e-b191-01eef8dd8379'
  },
  ...{
    ingestionMetadata: {
      sourceName: 'browser-typescript-ampli',
      sourceVersion: '2.0.0'
    }
  }
};

export interface LoadOptions {
  environment?: Environment;
  disabled?: boolean;
  client?: {
    apiKey?: string;
    configuration?: BrowserOptions;
    instance?: BrowserClient;
  }
}

export interface IdentifyProperties {
  /**
   * uuid assigned to client
   *
   * | Rule | Value |
   * |---|---|
   * | Regex | [0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12} |
   */
  client_id?: string;
  /**
   * uuid of attached customer in customer_properties
   *
   * | Rule | Value |
   * |---|---|
   * | Regex | [0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12} |
   */
  customer_id?: string;
  /**
   * email address on user JWT
   */
  email_address: string;
  /**
   * The domain portion (following @) of the email address associated with the client's account
   *
   * | Rule | Value |
   * |---|---|
   * | Regex | [^.]+\..+ |
   */
  email_domain?: string;
  /**
   * optional portion of email between + and @
   *
   * | Rule | Value |
   * |---|---|
   * | Max Length | 256 |
   */
  email_tag?: string;
  environment: string;
  /**
   * more detail on type of user interest/scope
   *
   * | Rule | Value |
   * |---|---|
   * | Unique Items | true |
   * | Item Type | string |
   */
  user_scope?: string[];
  /**
   * type of account via JWT auth
   *
   * | Rule | Value |
   * |---|---|
   * | Enum Values | client, customer, homebotter, unauthed |
   */
  user_type: "client" | "customer" | "homebotter" | "unauthed";
}

export interface PageViewedProperties {
  /**
   * Pattern of the URL of the page
   */
  page_location: string;
  /**
   * Actual URL of the page including IDs
   */
  page_path?: string;
  /**
   * Name of the page that describes the content
   */
  page_title?: string;
}

export interface QuestionAskedProperties {
  /**
   * When a client DMs their LO/REA about a Homebot topic
   */
  direct_message_topic: string;
}

export interface WinClickedProperties {
  /**
   * Client WIN CTA type
   *
   * | Rule | Value |
   * |---|---|
   * | Enum Values | should_you_sell, new_home, equity_smart_search, share, reserve, refi, home_insurance, pmi |
   */
  win_type:
    | "should_you_sell"
    | "new_home"
    | "equity_smart_search"
    | "share"
    | "reserve"
    | "refi"
    | "home_insurance"
    | "pmi";
}

export class Identify implements BaseEvent {
  event_type = 'Identify';

  constructor(
    public event_properties: IdentifyProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class Group implements BaseEvent {
  event_type = 'Group';
}

export class HomeAdded implements BaseEvent {
  event_type = 'Home Added';
}

export class HomeFavorited implements BaseEvent {
  event_type = 'Home Favorited';
}

export class MarketTempClicked implements BaseEvent {
  event_type = 'Market Temp Clicked';
}

export class PageViewed implements BaseEvent {
  event_type = 'Page Viewed';

  constructor(
    public event_properties: PageViewedProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class QuestionAsked implements BaseEvent {
  event_type = 'Question Asked';

  constructor(
    public event_properties: QuestionAskedProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class SearchCompleted implements BaseEvent {
  event_type = 'Search Completed';
}

export class SearchSaved implements BaseEvent {
  event_type = 'Search Saved';
}

export class WinClicked implements BaseEvent {
  event_type = 'WIN Clicked';

  constructor(
    public event_properties: WinClickedProperties,
  ) {
    this.event_properties = event_properties;
  }
}

export class YearsInHomeAdjusted implements BaseEvent {
  event_type = 'Years In Home Adjusted';
}

export type PromiseResult<T> = { promise: Promise<T | void> };

const getVoidPromiseResult = () => ({ promise: Promise.resolve() });

// prettier-ignore
export class Ampli {
  private disabled: boolean = false;
  private amplitude?: BrowserClient;

  get client(): BrowserClient {
    this.isInitializedAndEnabled();
    return this.amplitude!;
  }

  get isLoaded(): boolean {
    return this.amplitude != null;
  }

  private isInitializedAndEnabled(): boolean {
    if (!this.amplitude) {
      console.error('ERROR: Ampli is not yet initialized. Have you called ampli.load() on app start?');
      return false;
    }
    return !this.disabled;
  }

  /**
   * Initialize the Ampli SDK. Call once when your application starts.
   *
   * @param options Configuration options to initialize the Ampli SDK with.
   */
  load(options?: LoadOptions): PromiseResult<void> {
    this.disabled = options?.disabled ?? false;

    if (this.amplitude) {
      console.warn('WARNING: Ampli is already intialized. Ampli.load() should be called once at application startup.');
      return getVoidPromiseResult();
    }

    const env = options?.environment ?? 'development';
    const apiKey = options?.client?.apiKey ?? ApiKey[env];

    if (options?.client?.instance) {
      this.amplitude = options?.client?.instance;
    } else if (apiKey) {
      this.amplitude = amplitude.createInstance();
      return this.amplitude.init(apiKey, undefined, { ...DefaultConfiguration, ...options?.client?.configuration });
    } else {
      console.error("ERROR: ampli.load() requires 'environment', 'client.apiKey', or 'client.instance'");
    }

    return getVoidPromiseResult();
  }

  /**
   * Identify a user and set user properties.
   *
   * @param userId The user's id.
   * @param properties The user properties.
   * @param options Optional event options.
   */
  identify(
    userId: string | undefined,
    properties: IdentifyProperties,
    options?: EventOptions,
  ): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    if (userId) {
      options = {...options,  user_id: userId};
    }

    const amplitudeIdentify = new amplitude.Identify();
    const eventProperties = properties;
    if (eventProperties != null) {
      for (const [key, value] of Object.entries(eventProperties)) {
        amplitudeIdentify.set(key, value);
      }
    }
    return this.amplitude!.identify(
      amplitudeIdentify,
      options,
    );
  }

  /**
   * Set Group for the current user
   *
   * @param groupType The group type.
   * @param groupName The group name.
   * @param options Optional event options.
   */
  setGroup(groupType: string, groupName: string | string[], options?: EventOptions): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.setGroup(groupType, groupName, options);
  }

  /**
   * Identify a group and set group properties.
   *
   * @param groupType The group type.
   * @param groupName The group name.
   * @param options Optional event options.
   */
  groupIdentify(
    groupType: string,
    groupName: string | string[],
    options?: EventOptions,
  ): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    const amplitudeIdentify = new amplitude.Identify();
    return this.amplitude!.groupIdentify(groupType, groupName, amplitudeIdentify, options);
  }

  /**
   * Track event
   *
   * @param event The event to track.
   * @param options Optional event options.
   */
  track(event: Event, options?: EventOptions): PromiseResult<Result> {
    if (!this.isInitializedAndEnabled()) {
      return getVoidPromiseResult();
    }

    return this.amplitude!.track(event, undefined, options);
  }

  /**
   * Home Added
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Home%20Added)
   *
   * Client self added a new home via /home/new
   *
   * Owner: Leia Schultz
   *
   * @param options Amplitude event options.
   */
  homeAdded(
    options?: EventOptions,
  ) {
    return this.track(new HomeAdded(), options);
  }

  /**
   * Home Favorited
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Home%20Favorited)
   *
   * Client favorited a listing
   *
   * Owner: Leia Schultz
   *
   * @param options Amplitude event options.
   */
  homeFavorited(
    options?: EventOptions,
  ) {
    return this.track(new HomeFavorited(), options);
  }

  /**
   * Market Temp Clicked
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Market%20Temp%20Clicked)
   *
   * Client clicked CTA to access the Your Market panel
   *
   * Owner: Martha Bawn
   *
   * @param options Amplitude event options.
   */
  marketTempClicked(
    options?: EventOptions,
  ) {
    return this.track(new MarketTempClicked(), options);
  }

  /**
   * Page Viewed
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Page%20Viewed)
   *
   * Client viewed a specific page
   *
   * Owner: Martha Bawn
   *
   * @param properties The event's properties (e.g. page_location)
   * @param options Amplitude event options.
   */
  pageViewed(
    properties: PageViewedProperties,
    options?: EventOptions,
  ) {
    return this.track(new PageViewed(properties), options);
  }

  /**
   * Question Asked
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Question%20Asked)
   *
   * Client sends a direct message
   *
   * Owner: Leia Schultz
   *
   * @param properties The event's properties (e.g. direct_message_topic)
   * @param options Amplitude event options.
   */
  questionAsked(
    properties: QuestionAskedProperties,
    options?: EventOptions,
  ) {
    return this.track(new QuestionAsked(properties), options);
  }

  /**
   * Search Completed
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Search%20Completed)
   *
   * Client input a city, county, or zip to search for home listings
   *
   * Owner: Leia Schultz
   *
   * @param options Amplitude event options.
   */
  searchCompleted(
    options?: EventOptions,
  ) {
    return this.track(new SearchCompleted(), options);
  }

  /**
   * Search Saved
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Search%20Saved)
   *
   * Client saved their search
   *
   * Owner: Leia Schultz
   *
   * @param options Amplitude event options.
   */
  searchSaved(
    options?: EventOptions,
  ) {
    return this.track(new SearchSaved(), options);
  }

  /**
   * WIN Clicked
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/WIN%20Clicked)
   *
   * Client clicked on one of the WINs
   *
   * Owner: Leia Schultz
   *
   * @param properties The event's properties (e.g. win_type)
   * @param options Amplitude event options.
   */
  winClicked(
    properties: WinClickedProperties,
    options?: EventOptions,
  ) {
    return this.track(new WinClicked(properties), options);
  }

  /**
   * Years In Home Adjusted
   *
   * [View in Tracking Plan](https://data.amplitude.com/homebot/Homebot%20Tracking%20Plan/events/main/latest/Years%20In%20Home%20Adjusted)
   *
   * Client adjusted the years in home slider above refi options
   *
   * Owner: Leia Schultz
   *
   * @param options Amplitude event options.
   */
  yearsInHomeAdjusted(
    options?: EventOptions,
  ) {
    return this.track(new YearsInHomeAdjusted(), options);
  }
}

export const ampli = new Ampli();

// BASE TYPES
type BrowserOptions = amplitude.Types.BrowserOptions;

export type BrowserClient = amplitude.Types.BrowserClient;
export type BaseEvent = amplitude.Types.BaseEvent;
export type IdentifyEvent = amplitude.Types.IdentifyEvent;
export type GroupEvent = amplitude.Types.GroupIdentifyEvent;
export type Event = amplitude.Types.Event;
export type EventOptions = amplitude.Types.EventOptions;
export type Result = amplitude.Types.Result;
